import { useEffect } from 'react';
import { Summary } from '../../../../types';
import { Button, ErrorAlert, CopyButton, ExportDropdown } from '@components/ui';
import { Markdown } from '@components/ui/markdown';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { useSummarizer } from '@core/store/useSummarizer';
import { getApiUrl } from '../../../../lib/hooks/use-new-api';
import { useCompletion } from '../../../../lib/use-completion';
import { getCTADialogType, getUserFriendlyErrorMessage } from '../../../../lib/error-utils';
import { copyToClipboard, exportAsTextFile, exportAsPDF, generateFilename } from '@lib/content-utils';
import { useToast, toast } from '@components/ui/toast';

interface PromptDetailViewProps {
  summary: Summary;
}

export const PromptDetailView: React.FC<PromptDetailViewProps> = ({ summary }) => {
  const { activePrompt, setActivePrompt, selectedLanguage, updatePrompt, quota, triggerCTADialog } =
    useSummarizer();
  const { addToast } = useToast();

  // Determine which API to use
  const apiUrl = getApiUrl('/repurpose');

  // Format body based on API type
  const requestBody = {
    summary_id: summary.id,
    language: selectedLanguage,
    transcript: summary.transcript || '',
    user_id: quota?.user_id,
  };

  const {
    completion: content,
    loading,
    complete: stream,
    setCompletion: setContent,
    error,
  } = useCompletion({
    api: apiUrl,
    body: requestBody,
    onFinish: (_, content) => {
      console.log(activePrompt, content);
      if (activePrompt) {
        const updatedPrompt = {
          ...activePrompt,
          generatedContent: content,
        };
        updatePrompt(updatedPrompt);
      }
    },
    onError: (err) => {
      console.error('Repurpose generation error:', err);

      // Check if we should show CTA modal
      if (err.shouldShowCTA) {
        const ctaType = getCTADialogType(err);
        if (ctaType) {
          triggerCTADialog(ctaType, 'repurpose', err.message);
        }
      }
    },
  });
  console.log(content);
  useEffect(() => {
    if (activePrompt) {
      if (activePrompt.generatedContent) {
        setContent(activePrompt.generatedContent);
      } else {
        const finalPrompt = activePrompt.prompt.replace('{content}', summary.summary || '');
        const additionalParams = {
          title: activePrompt.title || 'Repurposed Content',
          prompt: finalPrompt,
          language: selectedLanguage,
        };
        stream(finalPrompt, additionalParams);
      }
    }
  }, [activePrompt?.id, summary.summary, selectedLanguage]);

  const handleRegenerate = () => {
    if (activePrompt) {
      const finalPrompt = activePrompt.prompt.replace('{content}', summary.summary || '');
      const additionalParams = {
        title: activePrompt.title || 'Repurposed Content',
        prompt: finalPrompt,
        language: selectedLanguage,
      };
      stream(finalPrompt, additionalParams);
    }
  };

  const handleExportAsTxt = async () => {
    try {
      if (!content) {
        addToast(toast.error('No content to export'));
        return;
      }

      const filename = generateFilename(
        activePrompt?.title ? `${activePrompt.title}_repurposed` : 'repurposed_content'
      );

      exportAsTextFile(content, filename);
      addToast(toast.success('Content exported as TXT successfully'));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      addToast(toast.error(`Failed to export: ${errorMessage}`));
    }
  };

  const handleExportAsPDF = async () => {
    try {
      if (!content) {
        addToast(toast.error('No content to export'));
        return;
      }

      const title = activePrompt?.title || 'Repurposed Content';
      const filename = generateFilename(
        activePrompt?.title ? `${activePrompt.title}_repurposed` : 'repurposed_content',
        'pdf'
      );

      addToast(toast.info('Generating PDF... This may take a moment.'));
      await exportAsPDF(content, title, filename);
      addToast(toast.success('Content exported as PDF successfully'));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      addToast(toast.error(`Failed to export PDF: ${errorMessage}`));
    }
  };

  return (
    <div className="h-full flex flex-col bg-white">
      <div className="p-4 border-b bg-gray-50 flex items-center justify-between sticky top-0 z-10">
        <Button
          variant="ghost"
          onClick={() => setActivePrompt(null)}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <p className="text-lg font-medium">{activePrompt?.title}</p>
        <div className="w-16"></div>
      </div>
      <div className="flex-1 overflow-y-auto p-6">
        {error && !loading && (
          <ErrorAlert
            message={getUserFriendlyErrorMessage(error)}
            details={error.details ? JSON.stringify(error.details, null, 2) : undefined}
            type={error.code === 'QUOTA_EXCEEDED' ? 'warning' : 'error'}
            title={
              error.code === 'QUOTA_EXCEEDED' ? 'Quota Exceeded' : 'Failed to generate content'
            }
            className="mb-4"
          />
        )}
        {loading && !content ? (
          <div className="flex items-center justify-center h-full">
            <div className="rounded-lg border bg-white p-4 shadow-lg dark:border-zinc-800 dark:bg-zinc-950">
              <div className="space-y-2">
                <div className="flex items-center space-x-1">
                  <div
                    className="h-4 animate-pulse rounded bg-muted-foreground/20"
                    style={{ width: '65%', animationDelay: '0ms' }}
                  ></div>
                  <div className="h-4 w-0.5 animate-pulse bg-brand-primary"></div>
                </div>
                <div className="flex items-center space-x-1 opacity-70">
                  <div
                    className="h-4 animate-pulse rounded bg-muted-foreground/15"
                    style={{ width: '50%', animationDelay: '200ms' }}
                  ></div>
                </div>
                <div className="flex items-center space-x-1 opacity-40">
                  <div
                    className="h-4 animate-pulse rounded bg-muted-foreground/10"
                    style={{ width: '35%', animationDelay: '400ms' }}
                  ></div>
                </div>
              </div>

              <div className="mt-3 flex items-center space-x-2">
                <div className="flex items-center space-x-1">
                  <svg
                    className="h-4 w-4 text-brand-primary"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-sm font-medium text-muted-foreground">
                    AI is thinking...
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : content ? (
          <Markdown>{content}</Markdown>
        ) : null}
      </div>
      <div className="p-4 border-t bg-gray-50 flex items-center justify-between sticky bottom-0 z-10">
        <div className="flex items-center gap-2">
          <CopyButton
            onCopy={() => {
              copyToClipboard(content || '');
            }}
          />
          <ExportDropdown
            onExportTxt={handleExportAsTxt}
            onExportPdf={handleExportAsPDF}
            disabled={!content || loading}
            loading={loading}
            variant="outline"
            size="sm"
            showText={true}
          />
          {/* <Languages
            isLoading={loading}
            value={selectedLanguage}
            onChange={setSelectedLanguage}
            icon={isMobile}
          /> */}
        </div>
        <Button onClick={handleRegenerate} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Regenerate Content
        </Button>
      </div>
    </div>
  );
};
